import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const toSafeMillimeters = (value, fallback, min = 15, max = 120) => {
  const size = Number(value);
  if (!Number.isFinite(size)) return fallback;
  return Math.min(Math.max(size, min), max);
};

// Reset the previous saved size. This roll uses a compact horizontal sticker.
const LABEL_PRINTER_SIZE_KEY = "pharmacy.label-printer-size-v3";
const DEFAULT_LABEL_PRINTER_SIZE = { widthMm: 50, heightMm: 25 };
const DEFAULT_LABEL_PRINTER_SETTINGS = {
  ...DEFAULT_LABEL_PRINTER_SIZE,
  qrSizeMm: 18,
  topMarginMm: 4,
  bottomMarginMm: 2,
  leftMarginMm: 2,
  rightMarginMm: 2,
  copies: 1,
};

const toSafeNumber = (value, fallback, min, max) =>
  toSafeMillimeters(value, fallback, min, max);

const sanitizeLabelPrinterSettings = (settings, fallback = DEFAULT_LABEL_PRINTER_SETTINGS) => {
  const widthMm = toSafeNumber(settings?.widthMm, fallback.widthMm, 20, 80);
  const heightMm = toSafeNumber(settings?.heightMm, fallback.heightMm, 15, 120);
  // Keep at least 5 mm of printable space on each axis. This prevents invalid
  // combined margins from leaving no room for the QR image.
  const maxHorizontalMargins = Math.max(0, widthMm - 5);
  const leftMarginMm = Math.min(
    toSafeNumber(settings?.leftMarginMm, fallback.leftMarginMm, 0, 20),
    maxHorizontalMargins,
  );
  const rightMarginMm = Math.min(
    toSafeNumber(settings?.rightMarginMm, fallback.rightMarginMm, 0, 20),
    maxHorizontalMargins - leftMarginMm,
  );
  const maxVerticalMargins = Math.max(0, heightMm - 5);
  const topMarginMm = Math.min(
    toSafeNumber(settings?.topMarginMm, fallback.topMarginMm, 0, 20),
    maxVerticalMargins,
  );
  const bottomMarginMm = Math.min(
    toSafeNumber(settings?.bottomMarginMm, fallback.bottomMarginMm, 0, 20),
    maxVerticalMargins - topMarginMm,
  );

  return {
    widthMm,
    heightMm,
    qrSizeMm: toSafeNumber(settings?.qrSizeMm, fallback.qrSizeMm, 5, 75),
    topMarginMm,
    bottomMarginMm,
    leftMarginMm,
    rightMarginMm,
    copies: toSafeNumber(settings?.copies, fallback.copies, 1, 100),
  };
};

export const getLabelPrinterSettings = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(LABEL_PRINTER_SIZE_KEY));
    if (saved) return sanitizeLabelPrinterSettings(saved);
  } catch {
    // Use defaults when storage is unavailable or invalid.
  }
  return DEFAULT_LABEL_PRINTER_SETTINGS;
};

export const setLabelPrinterSettings = (settings) => {
  const previous = getLabelPrinterSettings();
  const next = sanitizeLabelPrinterSettings(settings, previous);
  try {
    window.localStorage.setItem(LABEL_PRINTER_SIZE_KEY, JSON.stringify(next));
  } catch {
    // Printing still uses the selected settings if storage is unavailable.
  }
  return next;
};

export const getLabelPrinterSize = () => {
  const { widthMm, heightMm } = getLabelPrinterSettings();
  return { widthMm, heightMm };
};

export const setLabelPrinterSize = (widthMm, heightMm) => {
  const settings = setLabelPrinterSettings({ widthMm, heightMm });
  return { widthMm: settings.widthMm, heightMm: settings.heightMm };
};

const buildBarcodeLabelDataUrl = (value, widthMm, heightMm) => {
  const widthPx = Math.max(80, Math.round(widthMm * 16));
  const heightPx = Math.max(32, Math.round(heightMm * 16));
  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, widthPx, heightPx);

  const barCanvas = document.createElement("canvas");
  try {
    const encoded = {};
    JsBarcode(encoded, String(value), { format: "CODE128" });
    const modules = encoded.encodings.reduce((total, item) => total + item.data.length, 0);
    // Reserve ten narrow bars of white space on each side of the barcode.
    const barWidth = Math.max(1, Math.floor(widthPx / (modules + 20)));
    JsBarcode(barCanvas, String(value), {
      format: "CODE128",
      height: Math.round(heightPx * 0.65),
      width: barWidth,
      displayValue: true,
      textPosition: "bottom",
      fontSize: Math.round(heightPx * 0.16),
      textMargin: Math.round(heightPx * 0.04),
      margin: Math.round(heightPx * 0.04),
      marginLeft: barWidth * 10,
      marginRight: barWidth * 10,
      background: "#ffffff",
      lineColor: "#000000",
    });
  } catch {
    return "";
  }

  const scale = Math.min(widthPx / barCanvas.width, heightPx / barCanvas.height);
  const drawW = barCanvas.width * scale;
  const drawH = barCanvas.height * scale;
  ctx.drawImage(barCanvas, (widthPx - drawW) / 2, (heightPx - drawH) / 2, drawW, drawH);
  return canvas.toDataURL("image/png");
};

const buildQrDataUrl = (value) =>
  QRCode.toDataURL(String(value), {
    width: 256,
    margin: 4,
    color: { dark: "#000000", light: "#ffffff" },
  });

const renderLockedLabel = (kind, innerHtml) => `
  <section class="label ${kind}-only">
    <div class="print-area">
      ${innerHtml}
    </div>
  </section>
`;

const createLabelMarkup = async (
  { barcode, mode = "barcode" },
  { printableWidthMm, printableHeightMm, qrSizeMm },
) => {
  const code = String(barcode || "").trim();
  if (!code) return "";

  const safeMode = mode === "qr" ? "qr" : "barcode";
  const safeBarcode = escapeHtml(code);

  const codeWidthMm = safeMode === "qr" ? qrSizeMm : printableWidthMm;
  const codeHeightMm = safeMode === "qr"
    ? qrSizeMm
    : Math.min(18, printableHeightMm, printableWidthMm / 2.5);
  const dataUrl = safeMode === "qr"
    ? await buildQrDataUrl(code)
    : buildBarcodeLabelDataUrl(code, codeWidthMm, codeHeightMm);
  if (!dataUrl) return "";

  // Both modes print the same complete label image, including its white space.
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(printableWidthMm * 16);
  canvas.height = Math.round(printableHeightMm * 16);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const codeImage = new Image();
  codeImage.src = dataUrl;
  await codeImage.decode();
  const drawW = Math.round(codeWidthMm * 16);
  const drawH = Math.round(codeHeightMm * 16);
  ctx.imageSmoothingEnabled = safeMode !== "qr";
  ctx.drawImage(codeImage, (canvas.width - drawW) / 2, (canvas.height - drawH) / 2, drawW, drawH);

  return renderLockedLabel(
    safeMode,
    `<img class="label-img" src="${canvas.toDataURL("image/png")}" alt="${safeMode === "qr" ? "QR " : ""}${safeBarcode}" />`,
  );
};

/**
 * Print labels as individual physical-label pages in one print job.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr' }[]} labels
 * @param {{ widthMm?: number, heightMm?: number }} options
 */
export const printMedicineLabels = async (
  labels,
  { widthMm, heightMm } = {},
) => {
  const printableLabels = (Array.isArray(labels) ? labels : []).filter((label) =>
    Boolean(String(label?.barcode || "").trim()),
  );
  if (!printableLabels.length) return false;

  // XP-T361U supports labels up to 80 mm wide. These values must match the
  // label stock configured in the printer driver.
  const configuredSettings = sanitizeLabelPrinterSettings(
    { widthMm, heightMm },
    getLabelPrinterSettings(),
  );
  const labelWidthMm = configuredSettings.widthMm;
  const labelHeightMm = configuredSettings.heightMm;
  const printableWidthMm = Math.max(
    1,
    labelWidthMm - configuredSettings.leftMarginMm - configuredSettings.rightMarginMm,
  );
  const printableHeightMm = Math.max(
    1,
    labelHeightMm - configuredSettings.topMarginMm - configuredSettings.bottomMarginMm,
  );
  const qrSizeMm = Math.max(
    1,
    Math.min(configuredSettings.qrSizeMm, printableWidthMm, printableHeightMm),
  );
  const labelsMarkup = (
    await Promise.all(
      printableLabels.map((label) => createLabelMarkup(label, {
        printableWidthMm,
        printableHeightMm,
        qrSizeMm,
      })),
    )
  ).join("");

  const win = window.open("", "medicine-label-print");
  if (!win) return false;

  win.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>Medicine labels</title>
        <style>
          @page {
            size: ${labelWidthMm}mm ${labelHeightMm}mm;
            margin: 0;
          }
          * { box-sizing: border-box; }
          html, body {
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            margin: 0;
            padding: 0;
            background: #ffffff;
            overflow: hidden;
          }
          body {
            font-family: Arial, sans-serif;
            color: #000000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .label {
            position: relative;
            display: block;
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            max-width: ${labelWidthMm}mm;
            max-height: ${labelHeightMm}mm;
            overflow: hidden;
            padding: 0;
            margin: 0;
            background: #ffffff;
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .label:last-of-type {
            page-break-after: auto;
            break-after: auto;
          }
          .print-area {
            position: absolute;
            inset: 0;
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            padding: ${configuredSettings.topMarginMm}mm ${configuredSettings.rightMarginMm}mm ${configuredSettings.bottomMarginMm}mm ${configuredSettings.leftMarginMm}mm;
            overflow: hidden;
            text-align: center;
          }
          .qr-only .print-area,
          .barcode-only .print-area {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .label-img {
            display: block;
            width: ${printableWidthMm}mm;
            height: ${printableHeightMm}mm;
            flex-shrink: 0;
            object-fit: contain;
            break-inside: avoid;
          }
          @media print {
            html, body {
              width: ${labelWidthMm}mm !important;
              height: ${labelHeightMm}mm !important;
              overflow: hidden !important;
            }
            .label,
            .print-area {
              width: ${labelWidthMm}mm !important;
              height: ${labelHeightMm}mm !important;
              margin: 0 !important;
              overflow: hidden !important;
            }
          }
        </style>
      </head>
      <body>
        ${labelsMarkup}
        <script>
          const startPrint = () => {
            const printLabel = () => {
              // Keep this document open. Closing a print window programmatically
              // can cancel a Windows print job before the driver has accepted it.
              window.focus();
              window.print();
            };

            const images = Array.from(document.images);
            Promise.all(
              images.map((image) =>
                image.complete
                  ? Promise.resolve()
                  : new Promise((resolve) => {
                      image.addEventListener("load", resolve, { once: true });
                      image.addEventListener("error", resolve, { once: true });
                    }),
              ),
            ).then(() => window.setTimeout(printLabel, 100));
          };

          if (document.readyState === "complete") startPrint();
          else window.addEventListener("load", startPrint);
        <\/script>
      </body>
    </html>
  `);
  win.document.close();
  return true;
};

/**
 * Print copies of one medicine label. Kept for existing single-label callers.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr', copies?: number, widthMm?: number, heightMm?: number }} options
 */
export const printMedicineLabel = async ({
  barcode,
  name,
  mode = "barcode",
  copies,
  widthMm,
  heightMm,
}) => {
  const configuredCopies = getLabelPrinterSettings().copies;
  const labelCount = Math.max(1, Number.parseInt(copies ?? configuredCopies, 10) || 1);
  return printMedicineLabels(
    Array.from({ length: labelCount }, () => ({ barcode, name, mode })),
    { widthMm, heightMm },
  );
};

export default printMedicineLabel;
